import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, Loader2 } from "lucide-react";

import { AppSidebar } from "@/components/sidebar/app-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import {
  classifyText,
  ClassifyResult,
  Prediction,
} from "@/services/analysisApi";
import { softmax } from "./softmax";

const TEXT_MAX_LENGTH = 2000;

const LABEL_DESCRIPTIONS: Record<string, string> = {
  "Type A": "Intéressé",
  "Type A.2": "Intéressé sous condition / demande une précision",
  "Type B": "Intéressé + disponibilité",
  "Type C.1": "Pas à l'écoute — poste actuel",
  "Type C.2": "Pas à l'écoute — pas le moment",
  "Type C.3": "Pas à l'écoute — autre raison",
  "Type D.1": "Pas intéressé — rêve pro différent",
  "Type D.2": "Pas intéressé — non précisé",
  "Type D.3": "Pas intéressé — secteur",
  "Type E.1": "Hors-sujet",
  "Type E.2": "Vide / non interprétable",
};

const EXAMPLES = [
  "Bonjour, je suis intéressé par votre offre, je suis disponible jeudi 14h.",
  "Pas intéressé, merci.",
  "Je suis très bien dans mon poste actuel, recontactez-moi dans 6 mois.",
];

type RankedPrediction = Prediction & { probability: number };

export default function TestClassificationPage() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [result, setResult] = useState<ClassifyResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ranked = useMemo<RankedPrediction[]>(() => {
    if (!result) return [];
    const probs = softmax(result.predictions.map((p) => p.score));
    return result.predictions
      .map((p, i) => ({ ...p, probability: probs[i] }))
      .sort((a, b) => b.score - a.score);
  }, [result]);

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await classifyText(trimmed, navigate);
      setResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !loading) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const trimmedLength = text.trim().length;
  const submitDisabled = loading || trimmedLength === 0 || text.length > TEXT_MAX_LENGTH;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Test classification</h1>
          </div>
        </header>

        <div className="p-6 space-y-6 max-w-4xl">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <FlaskConical className="h-6 w-6" />
              Test du modèle de classification
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Simule un message de candidat à un recruteur et affiche la
              classification renvoyée par le classifieur SBERT + tête linéaire (
              <code className="text-xs">recrutauto/sentence-camembert-large-augmented</code>).
              Aucune donnée n'est sauvegardée.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Message du candidat</CardTitle>
              <CardDescription>
                Colle ou rédige un message tel que tu l'attendrais d'un candidat.
                Ctrl+Entrée pour classifier.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Bonjour, je serais intéressé par votre offre…"
                rows={6}
                maxLength={TEXT_MAX_LENGTH}
                disabled={loading}
                aria-label="Message du candidat à classifier"
              />
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs text-muted-foreground">
                  {text.length} / {TEXT_MAX_LENGTH} caractères
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    {EXAMPLES.map((ex, i) => (
                      <Button
                        key={i}
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={loading}
                        onClick={() => setText(ex)}
                      >
                        Exemple {i + 1}
                      </Button>
                    ))}
                  </div>
                  <Button onClick={handleSubmit} disabled={submitDisabled}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Classification…
                      </>
                    ) : (
                      "Classifier"
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {error && (
            <Card className="border-destructive">
              <CardContent className="pt-6 text-sm text-destructive">
                {error}
              </CardContent>
            </Card>
          )}

          {result && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Résultat</CardTitle>
                <CardDescription>
                  Probabilités calculées par softmax sur les 11 classes du
                  modèle. Score brut = logit retourné par la tête linéaire.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">
                    Classe prédite :
                  </span>
                  <Badge variant="default" className="text-sm">
                    {result.top}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {LABEL_DESCRIPTIONS[result.top] ?? ""}
                  </span>
                </div>

                <div className="space-y-2">
                  {ranked.map((p) => (
                    <div key={p.label} className="space-y-1">
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className={
                              p.label === result.top
                                ? "font-medium"
                                : "text-muted-foreground"
                            }
                          >
                            {p.label}
                          </span>
                          <span className="text-xs text-muted-foreground truncate">
                            {LABEL_DESCRIPTIONS[p.label] ?? ""}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 font-mono text-xs text-muted-foreground">
                          <span>logit {p.score.toFixed(3)}</span>
                          <span className="w-12 text-right">
                            {(p.probability * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <Progress value={p.probability * 100} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
